import { useEffect, useRef, useState, useCallback } from 'react';

// ─── Superseding requests for list screens ─────────────────────────────
//
// Every list screen fetched on each change of its search box and applied
// whichever response arrived LAST. Responses do not arrive in the order they
// were sent: typing "QT-2026-0158" fires a request per keystroke, the short
// prefix "Q" matches nothing (offer numbers are searched as digits) and
// answers slowly, and it lands after "QT-2026-0158" — so the screen said
// "0 offres" for an offer that exists. Seen live on CH 2026-09-28.
//
// Two independent guards, because either alone leaves a gap:
//   1. the previous request is ABORTED, so it stops costing the server and
//      the network;
//   2. a reply is APPLIED only if it belongs to the latest request, because
//      an abort can lose the race with a reply already in flight.

/**
 * Plain factory, no React — so the ordering rule is testable on its own.
 * `begin()` starts a request and returns its AbortSignal and an
 * `isCurrent()` that stays true only until the next `begin()`.
 */
export function createRequestGate() {
  let seq = 0;
  let controller = null;
  return {
    begin() {
      if (controller) controller.abort();
      controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const mine = ++seq;
      return { signal: controller ? controller.signal : undefined, isCurrent: () => mine === seq };
    },
    cancel() {
      if (controller) controller.abort();
      controller = null;
      seq += 1;
    },
  };
}

/** One gate per component instance, cancelled on unmount. */
export function useRequestGate() {
  const ref = useRef(null);
  if (!ref.current) ref.current = createRequestGate();
  useEffect(() => () => ref.current.cancel(), []);
  return useCallback(() => ref.current.begin(), []);
}

/**
 * The value, once it has stopped changing for `delay` ms. An empty value
 * passes through immediately: clearing a search should not make the user
 * wait to see the full list again.
 */
export function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (value === '' || value == null) { setDebounced(value); return undefined; }
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}
